import React, { useEffect } from 'react';
import { Box, Modal, Typography, Select, MenuItem } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useToast } from 'use-toast-mui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createBranchProduct, updateBranchProduct, deleteProductById } from '../../../../api/point';
import { errorMessages } from '../../../../utils/methods';
import CommonModalTitle from '../../../../form-components/CommonModalTitle';
import CommonText from '../../../../form-components/CommonText';
import { FormInputText } from '../../../../form-components/FormInputText';
import { FormInputPrice } from '../../../../form-components/FormInputPrice';
import { FormInputNumber } from '../../../../form-components/FormInputNumber';
import CommonModalButton from '../../../../form-components/CommonModalButton';

// Styled Components
const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '557px',
  backgroundColor: '#fff',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
});

const ModalHeader = styled(Box)({
  height: '48px',
  backgroundColor: '#262626',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 24px',
});

const CloseButton = styled(Box)({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    color: '#fff',
    width: '24px',
    height: '24px',
  },
});

const ModalContent = styled(Box)({
  padding: '32px',
});

const FormContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

const FormRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

const FormLabel = styled(CommonText)({
  width: '98px',
  minWidth: '98px',
});

const FormInputWrapper = styled(Box)({
  flex: 1,
  marginLeft: '12px',
});

const PriceInputWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: 1,
  marginLeft: '12px',
});

const PriceSuffix = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#111',
  minWidth: '14px',
});

const PeriodInputWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: 1,
  marginLeft: '12px',
});

const PeriodSelect = styled(Select)({
  width: '93px',
  height: '44px',
  borderRadius: '4px',
  fontSize: '14px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E0E0E0',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#BDBDBD',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#4AB3BC',
  },
  '& .MuiSelect-select': {
    padding: '12px 16px',
  },
});

const ButtonContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  gap: '16px',
  marginTop: '32px',
});

// Constants
const PERIOD_UNITS = [
  { value: 'MONTH', label: '개월' },
  { value: 'DAY', label: '일' },
];

// Main Component
const LockerEditModal = ({ open, onClose, branchId, user, editData, isEditMode = false }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { control, handleSubmit, reset, setValue } = useForm({
    defaultValues: {
      productName: '',
      price: '',
      periodValue: '',
      periodType: 'MONTH',
    },
  });

  useEffect(() => {
    if (open) {
      if (editData && isEditMode) {
        setValue('productName', editData.productName || '');
        setValue('price', editData.price ?? '');
        setValue('periodValue', editData.periodValue ?? '');
        setValue('periodType', editData.periodType || 'MONTH');
      } else {
        reset({
          productName: '',
          price: '',
          periodValue: '',
          periodType: 'MONTH',
        });
      }
    }
  }, [open, editData, isEditMode, setValue, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const invalidateProductQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['categories', branchId] });
    queryClient.invalidateQueries({ queryKey: ['products', branchId] });
  };

  // 락커 상품 생성 mutation
  const createMutation = useMutation({
    mutationFn: requestData => createBranchProduct(requestData, branchId, user.token),
    onSuccess: () => {
      toast.success(t('라커 상품이 등록되었습니다'));
      invalidateProductQueries();
      handleClose();
    },
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // 락커 상품 수정 mutation
  const updateMutation = useMutation({
    mutationFn: requestData => updateBranchProduct(requestData, editData?.productId, user.token),
    onSuccess: () => {
      toast.success(t('라커 상품이 수정되었습니다'));
      invalidateProductQueries();
      handleClose();
    },
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // 락커 상품 삭제 mutation
  const deleteMutation = useMutation({
    mutationFn: () => deleteProductById(user.token, editData?.productId),
    onSuccess: () => {
      toast.success(t('라커 상품이 삭제되었습니다'));
      invalidateProductQueries();
      handleClose();
    },
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // 생성/수정 공통 요청 데이터 빌드
  const buildRequestData = values => ({
    productCategoryType: 'LOCKER',
    productType: 'PERIOD',
    productName: values.productName,
    price: Number(values.price) || 0,
    periodValue: Number(values.periodValue) || 0,
    periodType: values.periodType,
    subscriptionYn: false,
  });

  const onSubmit = values => {
    const requestData = buildRequestData(values);

    if (isEditMode) {
      updateMutation.mutate(requestData);
    } else {
      createMutation.mutate(requestData);
    }
  };

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  const isLoading = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalContainer>
        {/* Header */}
        <ModalHeader>
          <CommonModalTitle variant="large">{isEditMode ? t('라커 상품 수정') : t('라커 상품 등록')}</CommonModalTitle>
          <CloseButton onClick={handleClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        {/* Content */}
        <ModalContent>
          <FormContainer>
            {/* 상품명 */}
            <FormRow>
              <FormLabel variant="label">{t('상품명')}</FormLabel>
              <FormInputWrapper>
                <FormInputText control={control} name="productName" placeholder={t('상품명을 입력해주세요.')} />
              </FormInputWrapper>
            </FormRow>

            {/* 상품 분류 (고정값: 라커) */}
            <FormRow>
              <FormLabel variant="label">{t('상품 분류')}</FormLabel>
              <FormInputWrapper>
                <FormInputText control={control} name="categoryDisplay" disabled staticValue={t('라커')} />
              </FormInputWrapper>
            </FormRow>

            {/* 상품 가격 */}
            <FormRow>
              <FormLabel variant="label">{t('상품 가격')}</FormLabel>
              <PriceInputWrapper>
                <FormInputPrice control={control} name="price" placeholder={t('상품 가격을 입력해주세요.')} />
                <PriceSuffix>{t('원')}</PriceSuffix>
              </PriceInputWrapper>
            </FormRow>

            {/* 유효 기간 */}
            <FormRow>
              <FormLabel variant="label">{t('유효 기간')}</FormLabel>
              <PeriodInputWrapper>
                <FormInputNumber control={control} name="periodValue" placeholder={t('유효 기간을 입력해주세요.')} />
                <Controller
                  name="periodType"
                  control={control}
                  render={({ field }) => (
                    <PeriodSelect {...field} IconComponent={KeyboardArrowDownIcon}>
                      {PERIOD_UNITS.map(unit => (
                        <MenuItem key={unit.value} value={unit.value}>
                          {t(unit.label)}
                        </MenuItem>
                      ))}
                    </PeriodSelect>
                  )}
                />
              </PeriodInputWrapper>
            </FormRow>
          </FormContainer>

          {/* Buttons */}
          <ButtonContainer>
            <CommonModalButton variant="cancel" onClick={handleClose}>
              {t('취소')}
            </CommonModalButton>
            {isEditMode && (
              <CommonModalButton variant="delete" onClick={handleDelete} disabled={isLoading}>
                {t('삭제')}
              </CommonModalButton>
            )}
            <CommonModalButton variant="submit" onClick={handleSubmit(onSubmit)} disabled={isLoading}>
              {isEditMode ? t('수정') : t('등록')}
            </CommonModalButton>
          </ButtonContainer>
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
};

export default LockerEditModal;
